import * as React from 'react';
import { DRAWER_PROPERTY_EDITOR_WIDTH } from '@src/framework/editor/src/js/constants';
import { ActionType } from '@src/framework/editor/src/js/constants/common-action-type';
import { DrawerName, WbEventNames } from '@src/framework/workbench/drawer/constants';
import { FIXED_KEYS_DOUBLE } from '@src/framework/workbench/schema/constant';
import { CommentShowAction, ShowActionSource } from '@src/js/constant';
import { IPresentationModel } from '@src/slide/domain/ui-model/element-modeles/Interface/presentation-model.interface';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import eventBus, { EventKeys } from '@src/slide/infrastructure/module/independent/event-emitter/instance';
import { BadgeService, DrawerService, EventEmitter, InjectorService, ToolbarService } from '@tencent/workbench';
import { IReactionDisposer, observable, reaction } from 'mobx';
import { createRef, RefObject } from 'react';
import { IDrawerStateManager } from '../../../drawer-state-manager/core/drawer-state-manager.interface';
import { BaseCommentManager } from '../base';
import { CommentActionType, CommentGroupInfoMap, ICommentActionCallbackProps } from '../base/index.interface';
import { ICommentManager, ICommentManagerServiceDelegates } from './comment-manager.interface';
import CommentDrawerTitle from './components/comment-drawer/comment-drawer-title';
import CommentDrawer from './components/comment-drawer';
import { IRightSideBarManager } from '../../../right-side-bar-manager/right-side-bar-manager.interface';
import { EventCodeType, TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import DrawerTopTab from '@framework/workbench/drawer/components/drawer-top-tab';
import type { Subscription } from 'rxjs';
import { commonRequestIdleCallback } from '@src/slide/infrastructure/module/dependent/idle-loader';
import type { IHistoryManager } from '../../../history-manager/core/history-manager.interface';
import { IMasterLayoutEditService } from '../../../master-layout-edit-service/master-layout-edit-service.interface';

const WbLocalStorageRemoveBageKey = 'workbench:badge-removed';  // workbench存储的移出过的红点的ke

export class CommentManager extends BaseCommentManager implements ICommentManager {
    @observable public isCommentDrawerShown = false;    // 批注侧边栏是否显示

    protected services: ICommentManagerServiceDelegates;

    @observable private commentShowAction = CommentShowAction.NO_ACTION;
    private showActionSource: ShowActionSource | undefined;
    private drawerService: DrawerService;
    private toolbarService: ToolbarService;
    private badgeService: BadgeService;
    private drawerStateManager?: IDrawerStateManager;
    private rightSideBarManager?: IRightSideBarManager;
    private historyManager?: IHistoryManager;
    private masterLayoutEditService?: IMasterLayoutEditService;
    private selectedModelReaction: IReactionDisposer | null;
    private commentDrawerRef: RefObject<any>;
    private commentDrawerTitleRef: RefObject<any>;
    private currentSlide: ISlideModel | undefined;
    private activeCommentSubscriber: () => void;    // 切换批注组
    private totalCommentGroupSize = 0;
    private onOpenCallBacks: (() => void)[] = []; //  侧边栏打开后的回调
    private isSlideChangeByActiveChange: boolean;   // currentSlide的变更是由点击上一条/下一条批注引起的
    private isPrevAction?: boolean; // 是否点击上一条
    private hoverElementAnchorReaction: IReactionDisposer | null;   // 通过hover元素的角标，来hover批注侧边栏的对应的批注组
    private activeElementAnchorReaction: IReactionDisposer | null;  // 通过点击元素的角标，来active批注侧边栏的对应的批注组中的第一个批注组
    private eventEmitter: EventEmitter;
    private rightDrawerOpenListener: Subscription | undefined;

    public setServiceDelegates(services: ICommentManagerServiceDelegates) {
        this.services = {
            ...this.services,
            ...services,
        };
    }

    public setRightSideBarManagerDelegate = (rightSideBarManager: IRightSideBarManager) => {
        if (!this.rightSideBarManager) {
            this.rightSideBarManager = rightSideBarManager;
        }
    };

    public setHistoryManagerDelegate(historyManager: IHistoryManager): void {
        if (!this.historyManager) {
            this.historyManager = historyManager;
        }
    }

    public setMasterLayoutEditServiceDelegate(masterLayoutEditService: IMasterLayoutEditService): void {
        this.masterLayoutEditService = masterLayoutEditService;
    }

    public init = (drawerStateManager?: IDrawerStateManager) => {
        this.drawerStateManager = drawerStateManager;
        this.drawerService = InjectorService.get(DrawerService);
        this.toolbarService = InjectorService.get(ToolbarService);
        this.badgeService = InjectorService.get(BadgeService);
        this.eventEmitter = InjectorService.get(EventEmitter);
        this.commentDrawerRef = createRef();
        this.commentDrawerTitleRef = createRef();
        this.clearBadgeStorage();
        reaction(
            () => this.isCommentDrawerShown,
            async (show) => {
                if (show) {
                    this.updateObserverForModel();
                    await this.updateCommentGroupInfoMap();
                    this.showCommentDrawer();
                    this.drawerStateManager?.openTab(DrawerName.COMMENT_DRAWER);
                    if (this.badgeService.hasBadgesOn('toolbar', ActionType.SHOW_COMMENT)) {
                        this.report(TDWActionType.REMIND_CLK, EventCodeType.DOC_PPT_COMMENTS_REMIND_CLK);
                    }
                    this.report(TDWActionType.OPEN, EventCodeType.DOC_PPT_COMMENTS_SLIDEBAR_OPEN);
                } else {
                    this.drawerStateManager?.closeRightDrawer(DrawerName.COMMENT_DRAWER);
                    this.report(TDWActionType.CLOSE, EventCodeType.DOC_PPT_COMMENTS_SLIDEBAR_OPEN);
                }
                window.dispatchEvent(new Event('resize'));
                // 刷新查看批注按钮的状态
                this.toolbarService.$forceUpdate();
            },
        );
        reaction(
            () => this.commentShowAction,
            this.toogleCommentDrawerWithAction,
        );
        // 监听页面变更。由于未打开侧边栏的情况下默认显示批注，所以需要在此处做监听
        reaction(
            () => this.services.selectionService.currentSlide,
            this.handleCurrentSlideChange,
            { fireImmediately: true },
        );
        reaction(
            () => this.isCommentAnchorShown,
            this.handleCommentAnchorShown,
        );
        // 监听协同数据
        eventBus.on(EventKeys.NOTICE_COMMENT_CHANGE, this.handleNoticeCommentChange);
        // 监听slideModel的sdk-ui转换。 case：修订记录
        eventBus.on(EventKeys.SLIDE_MODEL_CONVERTED_COMMENT, this.handleSlideModelChange);
        // 闲时拉取文档批注
        commonRequestIdleCallback?.(() => {
            const updatePromises = this.getCurrentSlide()?.parent?.children
                .map(slide => this.updateCommentGroupInfoMap(slide as ISlideModel)) || [];
            Promise.all(updatePromises).then(() => {
                eventBus.emit(EventKeys.COMMENT_GROUP_INFO_CHANGED);
            });
        });
    };

    public setIsCommentDrawerShown = (isCommentDrawerShown: boolean) => {
        this.isCommentDrawerShown = isCommentDrawerShown;
    };

    public setCommentShowAction = (commentShowAction: CommentShowAction, showActionSource?: ShowActionSource) => {
        this.commentShowAction = commentShowAction;
        this.showActionSource = showActionSource;
    };

    public getDrawerWidth = (): number => DRAWER_PROPERTY_EDITOR_WIDTH;

    public shouldHideComment = (): boolean => !!(this.masterLayoutEditService?.isMasterEditType
        || this.historyManager?.isShowHistoryPanel);

    protected afterCommentActionCallback = (props: ICommentActionCallbackProps) => {
        const { commentActionType, isCommentGroup, isFirstComment } = props;
        if (this.isCommentDrawerShown) {
            this.refreshCommentDrawer();
        }

        // 数据上报
        switch (commentActionType) {
            case CommentActionType.Add:
                if (isCommentGroup) {
                    this.report(
                        TDWActionType.ADD_COMMENTS_SUCCESS, EventCodeType.DOC_PPT_COMMENTS_ADD_COMMENTS_SUCCESS,
                        this.showActionSource,
                    );
                    this.showActionSource = undefined;
                } else {
                    this.report(TDWActionType.REPLY, EventCodeType.DOC_PPT_COMMENTS_SLIDEBAR_REPLY);
                }
                break;
            case CommentActionType.Delete:
                // 删除批注组的场景包括工具栏和侧边栏，分别各自上报
                if (!isCommentGroup) {
                    this.report(TDWActionType.DELETE_REPLY, EventCodeType.DOC_PPT_COMMENTS_SLIDEBAR_DELETE_REPLY);
                }
                break;
            case CommentActionType.Update:
                if (isFirstComment) {
                    this.report(TDWActionType.EDIT_CMMENTS, EventCodeType.DOC_PPT_COMMENTS_SLIDEBAR_EDIT_CMMENTS);
                } else {
                    this.report(TDWActionType.EDIT_REPLY, EventCodeType.DOC_PPT_COMMENTS_SLIDEBAR_EDIT_REPLY);
                }
                break;
            default:
                break;
        }
    };

    private showCommentDrawer = () => {
        this.registPropertyDrawerListener();
        this.totalCommentGroupSize = this.getTotalCommentGroupSize();
        const hoverCommentGroupIds = this.hoverElementId ? this.commentGroupIdsMap.get(this.hoverElementId) : undefined;
        this.drawerService.showRightDrawer({
            name: DrawerName.COMMENT_DRAWER,
            content: <DrawerTopTab>
                <CommentDrawerTitle
                    isJumpBtnDisabled={!this.getCurrentSlide()?.commentGroupMap?.size}
                    jumpToPrevComment={this.jumpToPrevComment}
                    jumpToNextComment={this.jumpToNextComment}
                    ref={this.commentDrawerTitleRef}
                    onClose={this.onClose}
                />
                <CommentDrawer
                    commentGroupInfoMap={this.getCurrentSlide()?.commentGroupInfoMap}
                    totalCommentGroupSize={this.totalCommentGroupSize}
                    selectionService={this.services.selectionService}
                    addComment={this.addComment}
                    updateComment={this.updateComment}
                    deleteComment={this.deleteComment}
                    deleteCommentGroup={this.deleteCommentGroup}
                    addCommentGroup={this.addCommentGroup}
                    jumpToNextComment={this.jumpToNextComment}
                    setActiveGroupId={this.setActiveGroupId}
                    setHoverGroupId={this.setHoverGroupId}
                    changeCurrentSlideByActiveChange={(isPrev?: boolean) => {
                        this.isSlideChangeByActiveChange = true;
                        this.isPrevAction = isPrev;
                    }}
                    setPendingCommentGroup={this.setPendingCommentGroup}
                    activeGroupId={this.activeGroupId}
                    hoverCommentGroupIds={hoverCommentGroupIds}
                    isAddCommentDisabled={!this.isSupportAddComment()}
                    isJumpBtnDisabled={this.getCommentCount() === 0}
                    ref={this.commentDrawerRef}
                /></DrawerTopTab>,
            style: {
                width: DRAWER_PROPERTY_EDITOR_WIDTH,
                borderBottom: '1px solid #e4e4e4',
                padding: '0px',
                backgroundColor: '#fff',
            },
            onClose: this.handleCommentDrawerClose,
            onOpen: () => {
                this.onOpenCallBacks.pop()?.();
            },
        });
        this.removeBadgeOn();
        this.toolbarService.changeRibbonTab(FIXED_KEYS_DOUBLE.TOOLBAR_COMMENT);
        this.drawerStateManager?.handleDrawerHolderVisiable(true);
    };

    private toogleCommentDrawerWithAction = () => {
        if (this.commentShowAction === CommentShowAction.NO_ACTION) return;
        switch (this.commentShowAction) {
            case CommentShowAction.INSERT_COMMENT:
                if (this.isCommentDrawerShown) {
                    this.handleInsertCommentAction();
                } else {
                    this.setIsCommentDrawerShown(true);
                    this.onOpenCallBacks.push(this.handleInsertCommentAction);
                }
                this.report(
                    TDWActionType.ADD_COMMENTS, EventCodeType.DOC_PPT_COMMENTS_ADD_COMMENTS,
                    this.showActionSource,
                );
                break;
            case CommentShowAction.PREV_COMMENT_GROUP:
                this.isCommentDrawerShown ? this.jumpToPrevComment() : this.setIsCommentDrawerShown(true);
                break;
            case CommentShowAction.NEXT_COMMENT_GROUP:
                this.isCommentDrawerShown ? this.jumpToNextComment() : this.setIsCommentDrawerShown(true);
                break;
            case CommentShowAction.LEFT_SLIDE_COMMENT_ANCHOR:
                this.handleLeftCommentAnchorChange();
                break;
            case CommentShowAction.EDIT_SLIDE_COMMENT_ANCHOR:
                this.setIsCommentDrawerShown(true);
                break;
            default:
                break;
        }
        this.commentShowAction = CommentShowAction.NO_ACTION;
    };

    private handleLeftCommentAnchorChange() {
        this.setIsCommentDrawerShown(!this.isCommentDrawerShown);
        if (!this.isCommentDrawerShown) {
            this.drawerStateManager?.closeTab(DrawerName.COMMENT_DRAWER);
        }
    }

    private handleInsertCommentAction = () => {
        const commentDrawerProps = this.addCommentGroup();
        if (!commentDrawerProps) return;
        this.commentDrawerRef.current?.updateCommentDrawerProps(commentDrawerProps);
        setTimeout(() => {
            this.commentDrawerRef.current?.scrollToView();
        });
    };

    private onClose = () => {
        this.handleCommentDrawerClose();
        this.drawerStateManager?.closeTab(DrawerName.COMMENT_DRAWER);
    };

    private handleCommentDrawerClose = () => {
        if (this.isOtherDrawerOpen()) {
            this.rightDrawerOpenListener = this.eventEmitter.listen(WbEventNames.RIGHT_DRAWER_CLOSED, () => {
                this.showCommentDrawer();
                this.rightDrawerOpenListener?.unsubscribe();
            });
        } else {
            this.setIsCommentDrawerShown(false);
        }
        this.setActiveGroupId('');
        this.setHoverGroupId('');
        this.drawerStateManager?.handleDrawerHolderVisiable(false);
        this.removePropertyDrawerListener();
    };

    private isOtherDrawerOpen = (): boolean | undefined => this.isCommentDrawerShown
        && !(this.drawerService.isRightOpening(DrawerName.SLIDE_PROPERTY_DRAWER)
            || this.drawerService.isRightOpening(DrawerName.ANIMATION_RIGHT_DRAWER)
            || this.drawerService.isRightOpening(DrawerName.COMMENT_DRAWER)
            || this.drawerService.isRightOpening(DrawerName.SELECT_WINDOW_RIGHT_DRAWER)
        )
        && this.drawerService.isRightOpening();

    private registPropertyDrawerListener = () => {
        // 监听选中元素变更
        this.selectedModelReaction = reaction(
            () => this.getSelectedModel(),
            (selectedModel) => {
                const isUnsupportAddComment = !this.isSupportAddComment();
                this.commentDrawerRef.current?.updateBtnState({
                    isAddCommentDisabled: isUnsupportAddComment,
                });
                if (selectedModel?.type === UIModelType.SLIDE) {
                    this.commentDrawerRef.current?.updateActiveGroupId({ activeGroupId: '' });
                    return;
                }
                if (isUnsupportAddComment) return;
                const groupIdList = this.getSelectedCommentGroupIdList();
                // 选中的元素有多个批注时，仅高亮第一个批注
                this.commentDrawerRef.current?.updateActiveGroupId({
                    activeGroupId: groupIdList.length === 0 ? '' : groupIdList[0],
                });
            },
        );
        this.activeCommentSubscriber = eventBus.on(EventKeys.ACTIVE_COMMENT_CHANGE, this.handleCommentActiveChange);
        this.hoverElementAnchorReaction = reaction(
            () => this.hoverElementId,
            () => {
                const hoverCommentGroupIds = this.hoverElementId
                    ? this.commentGroupIdsMap.get(this.hoverElementId)
                    : undefined;
                this.commentDrawerRef.current?.updateHoverCommentGroupIds(hoverCommentGroupIds);
            },
        );
        this.activeElementAnchorReaction = reaction(
            () => this.activeGroupId,
            () => {
                if (this.activeGroupId) {
                    this.commentDrawerRef.current?.updateActiveGroupId({ activeGroupId: this.activeGroupId });
                }
            },
        );
    };

    private removePropertyDrawerListener = () => {
        this.selectedModelReaction?.();
        this.removeObserverForModel(this.currentSlide);
        this.activeCommentSubscriber();
        this.hoverElementAnchorReaction?.();
        this.activeElementAnchorReaction?.();
    };

    private handleNoticeCommentChange = async (payLoad) => {
        await this.updateCommentCacheByNotice(payLoad);
        // 未产生协同变更时，不做处理
        if (this.collabAddCommentCount === 0
            && this.collabUpdateCommentCount === 0
            && this.collabDelCommentCount === 0) {
            return;
        }
        if (this.isCommentDrawerShown) {
            this.refreshCommentDrawer();
        } else {
            if (this.isCommentNotifyFlagShown && (this.collabAddCommentCount > 0
                || this.collabUpdateCommentCount > 0)) {
                // 当协同产生新增或者更新批注时，且未打开侧边栏的情况下增加红点通知
                this.showBadgeOn();
            }
        }
    };

    private refreshCommentDrawer = () => {
        this.totalCommentGroupSize = this.getTotalCommentGroupSize();
        this.commentDrawerRef.current?.updateCommentDrawerProps({
            commentGroupInfoMap: this.getCurrentSlide()?.commentGroupInfoMap,
            totalCommentGroupSize: this.totalCommentGroupSize,
        });
    };

    private getTotalCommentGroupSize = (): number => (this.currentSlide?.parent as IPresentationModel)?.children
        .reduce((pre, acc) => pre + (acc.commentGroupMap?.size ?? 0), 0);

    private handleCommentActiveChange = (payLoad) => {
        this.commentDrawerRef.current?.updateActiveGroupId(payLoad);
    };

    private updateObserverForModel = () => {
        this.removeObserverForModel(this.currentSlide);
        this.currentSlide = this.services.selectionService.currentSlide;
        this.addObserverForModel(this.currentSlide);
    };

    private addObserverForModel(model?: ISlideModel) {
        model?.changeSetEngine?.addObserveForChangeAtPath('commentGroupMap', (cs?: any, params?: any) => {
            this.handleCommentGroupChange();
        });
    }

    private removeObserverForModel(model?: ISlideModel) {
        model?.changeSetEngine?.removeObserveForChangeAtPath('commentGroupMap', (cs?: any, params?: any) => {
            this.handleCommentGroupChange();
        });
    }

    private handleCommentAnchorShown = async () => {
        if (!this.isCommentAnchorShown) return;
        this.updateObserverForModel();
        await this.handleCommentGroupChange();
    };

    private handleCurrentSlideChange = async () => {
        // 既不显示角标，也未打开批注侧边栏的情况下，无需做处理。
        if (!this.isCommentAnchorShown && !this.isCommentDrawerShown) return;
        // 删除所有页面，当前选中的slide为空
        if (!this.services.selectionService.currentSlide) return;
        this.updateObserverForModel();
        const commentGroupInfoMap = await this.handleCommentGroupChange();
        this.handleSlideChangeByActiveChange(commentGroupInfoMap);
    };

    private handleSlideModelChange = ({ slideModel }) => {
        if (this.historyManager?.isShowingHistory) return;
        if (!slideModel) return;
        const commentGroupMap = slideModel?.commentGroupMap;
        if (!commentGroupMap?.size) return;
        const slideId = slideModel.id;
        if (!slideId) return;
        const commentGroupInfoMap = this.commentGroupInfoCache.get(slideId);
        if (!commentGroupInfoMap?.size) return;
        const validComentGroupInfoMap = new Map();
        commentGroupInfoMap.forEach((commentGroupInfo, groupId) => {
            if (commentGroupMap.get(groupId)) {
                validComentGroupInfoMap.set(groupId, commentGroupInfo);
            }
        });
        if (validComentGroupInfoMap.size > 0) {
            slideModel.commentGroupInfoMap = validComentGroupInfoMap;
        }
    };

    /**
     * 处理当点击批注侧边栏“上一条/下一条”按钮时引起的切页场景
     */
    private handleSlideChangeByActiveChange = (commentGroupInfoMap?: CommentGroupInfoMap) => {
        const size = commentGroupInfoMap?.size ?? 0;
        if (this.isSlideChangeByActiveChange && size > 0) {
            const groupIdList = Array.from(commentGroupInfoMap!.keys());
            if (this.isPrevAction) {
                this.commentDrawerRef.current?.updateActiveGroupId({ activeGroupId: groupIdList[size - 1] });
                setTimeout(() => {
                    this.commentDrawerRef.current?.scrollToView();
                });
            } else {
                this.commentDrawerRef.current?.updateActiveGroupId({ activeGroupId: groupIdList[0] });
            }
            // 重置数据
            this.isSlideChangeByActiveChange = false;
            this.isPrevAction = false;
        }
    };

    /**
     * 响应commentGroup的变更
     */
    private handleCommentGroupChange = async (): Promise<CommentGroupInfoMap | undefined> => {
        this.updateCommentGroupIdMap();
        if (this.isCommentDrawerShown) {
            this.commentDrawerRef.current?.updateBtnState({
                isJumpBtnDisabled: this.getCommentCount() === 0,
            });
            this.commentDrawerTitleRef.current?.updateBtnState({
                isJumpBtnDisabled: this.getCommentCount() === 0,
            });
            return await this.updateCommentDrawerData();
        }
    };

    /**
     * 更新批注侧边栏所需数据
     */
    private updateCommentDrawerData = async (): Promise<CommentGroupInfoMap | undefined> => {
        this.setPendingCommentGroup(undefined);
        await this.updateCommentGroupInfoMap();
        const commentGroupInfoMap = this.getCurrentSlide()?.commentGroupInfoMap;
        this.commentDrawerRef.current?.updateCommentDrawerProps({
            commentGroupInfoMap,
            pendingCommentGroup: this.pendingCommentGroup,
        });
        return commentGroupInfoMap;
    };

    /**
     * 工具栏显示批注变更（包括新增和更新）通知红点
     */
    private showBadgeOn = () => {
        this.badgeService.showBadgeOn('ribbonTab', {
            key: FIXED_KEYS_DOUBLE.TOOLBAR_COMMENT,
            expire: Infinity,
        });
        this.badgeService.showBadgeOn('toolbar', {
            key: ActionType.SHOW_COMMENT,
            expire: Infinity,
            group: FIXED_KEYS_DOUBLE.TOOLBAR_COMMENT,
        });
        this.report(TDWActionType.REMIND_VIEW, EventCodeType.DOC_PPT_COMMENTS_REMIND_VIEW);
    };

    /**
     * 打开批注侧边栏后，清除批注红点
     */
    private removeBadgeOn = () => {
        this.clearBadgeStorage();
        this.badgeService.removeBadgeOn('toolbar', ActionType.SHOW_COMMENT);
        this.badgeService.removeBadgeOn('ribbonTab', FIXED_KEYS_DOUBLE.TOOLBAR_COMMENT);
    };

    /**
     * 清除locaStorage缓存的批注红点的记录。
     */
    // FIXME 由于workbench不会自动清除，只会展示一次红点，所以需要手动清除
    private clearBadgeStorage = () => {
        try {
            const removeBages = localStorage.getItem(WbLocalStorageRemoveBageKey);
            if (removeBages) {
                const noCommentBageList = JSON.parse(removeBages).filter(item => !item.includes('comment'));
                localStorage.setItem(WbLocalStorageRemoveBageKey, JSON.stringify(noCommentBageList));
            }
        } catch (error) {
            console.error(`Failed to clear comment badge:\n${error}`);
        }
    };

    private report(action: string, eventcode: string, ver5?: number) {
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.COMMENTS_SLIDEBAR,
            action,
            eventcode,
            ver5,
        });
    }
}
